import { GraphQLFormattedError } from 'graphql';
import { ApolloError } from '@apollo/client';
import {
  CreditProfileAuthStatus,
  CreditProfileAuthStatusProfile,
  CreditProfileAuthThinFileReason,
  TuCreditReportIdentityProfile,
} from '@generated-gql/generated-types';

export const isActive = (
  profile?: CreditProfileAuthStatusProfile | null,
): boolean => profile?.status === CreditProfileAuthStatus.Active;

// User passed verification, but did not complete TU enrollment
export const isAuthenticated = (
  profile?: CreditProfileAuthStatusProfile | null,
): boolean => profile?.status === CreditProfileAuthStatus.Authenticated;

export const isPending = (
  profile?: CreditProfileAuthStatusProfile | null,
): boolean => profile?.status === CreditProfileAuthStatus.Pending;

export const isDeactivated = (
  profile?: CreditProfileAuthStatusProfile | null,
): boolean => profile?.status === CreditProfileAuthStatus.Deactivated;

/*
 * It's possible for the has_thin_file to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the PENDING status when determining if a user is in a thin file state (the flag gets set by transunion when the
 * status transitions to PENDING)
 */
export const isThinFile = (profile?: CreditProfileAuthStatusProfile): boolean =>
  !!profile &&
  profile.status === CreditProfileAuthStatus.Pending &&
  profile.hasThinFile === true;

export const isBlocked = (profile: CreditProfileAuthStatusProfile): boolean =>
  profile.status === CreditProfileAuthStatus.Blocked;

/*
 * It's possible for the not_enough_data to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the ACTIVE status when determining if a user is in a thin file state (the flag gets set by transunion when the
 * status transitions to ACTIVE)
 */
export const notEnoughData = (
  profile: CreditProfileAuthStatusProfile,
): boolean =>
  profile.status === CreditProfileAuthStatus.Active &&
  profile.notEnoughData === true;

/*
 * It's possible for the is_deceased to remain set after the TU authentication status has changed to deactivated or inactive
 * so we explicitly check for the ACTIVE status when determining if a user is in a thin file state (the flag gets set by transunion when the
 * status transitions to ACTIVE)
 */
export const isDeceased = (profile: CreditProfileAuthStatusProfile): boolean =>
  profile.status === CreditProfileAuthStatus.Active &&
  profile.isDeceased === true;

// Rate limit error: too many connection attempts with TU
export const isTransunionDuplicate = (
  profile: CreditProfileAuthStatusProfile,
): boolean =>
  isThinFile(profile) &&
  profile.thinFileReason === CreditProfileAuthThinFileReason.Duplicate;

export const isTransunionNoData = (
  profile: CreditProfileAuthStatusProfile,
): boolean =>
  isThinFile(profile) &&
  profile.thinFileReason === CreditProfileAuthThinFileReason.NoData;

export const isTransunionFailVerification = (
  profile: CreditProfileAuthStatusProfile,
): boolean =>
  isThinFile(profile) &&
  profile.thinFileReason === CreditProfileAuthThinFileReason.FailVerification;

export const isTransunionOtherThinFile = (
  profile: CreditProfileAuthStatusProfile,
): boolean =>
  isThinFile(profile) &&
  profile.thinFileReason === CreditProfileAuthThinFileReason.Other;

/**
 * Returns true if the user is in a credit score blocked state
 * @param profile
 * @returns
 */
export const isTUBlocked = (
  profile?: CreditProfileAuthStatusProfile | null,
): boolean => {
  if (!profile) {
    return false;
  }
  return (
    isBlocked(profile) ||
    // passed TU verification, but didn't complete enrollment
    isAuthenticated(profile) ||
    isDeceased(profile) ||
    notEnoughData(profile) ||
    isTransunionDuplicate(profile) ||
    isTransunionNoData(profile) ||
    isTransunionFailVerification(profile) ||
    isTransunionOtherThinFile(profile)
  );
};

/**
 * Returns true if the user qualifies for OTP and must select how to verify
 * @param profile
 * @returns
 */
export const hasOtpAuthenticationQuestions = (
  profile: CreditProfileAuthStatusProfile,
): boolean => {
  return (
    profile.questions &&
    profile.questions?.[0]?.text.includes(
      'Please select your preferred method of Authentication',
    )
  );
};

/**
 * Returns true if the user must now provide their one time passcode (OTP)
 * @param profile
 * @returns
 */
export const hasOtpPasscodeQuestions = (
  profile: CreditProfileAuthStatusProfile,
): boolean => {
  return (
    profile.questions &&
    profile.questions?.[0]?.text.includes('Enter the passcode you received')
  );
};

const fullSsnRegex = /^[0-9]{9}$/;

export const isFullSsnValid = (str) => fullSsnRegex.test(str);
export const isSsnNumbersMatching = (ssn, confirmSsn) => ssn === confirmSsn;

const getGraphQLError = (error?: ApolloError): GraphQLFormattedError | null => {
  if (!error?.graphQLErrors || error.graphQLErrors.length === 0) {
    return null;
  }
  return error.graphQLErrors[0];
};

/**
 * If an error was returned for `initializeCreditProfile`, parse
 * the error returned to see if it's due to already having another
 * NW account associated with the SSN.
 * @param error
 * @returns the email associated
 */
export const getDuplicateEmailFromError = (
  error?: ApolloError,
): string | null => {
  const gqlError = getGraphQLError(error);
  if (gqlError) {
    const responseData = gqlError.extensions?.response?.body?.error?.data ?? {};
    const { internal_message, blocking_emails = [] } = responseData;
    return blocking_emails.length > 0 &&
      internal_message === 'Active TU Enrollment already exists for SSN'
      ? blocking_emails[0]
      : null;
  }
  return null;
};

/**
 * Given ApolloError from the `initializeCreditProfile` mutation,
 * return user facing copy
 * @param error
 * @returns User facing error message
 */
export const generateCreateTUCustomerErrorMessage = (
  error?: ApolloError,
): string => {
  const gqlError = getGraphQLError(error);
  if (gqlError) {
    const responseData = gqlError.extensions?.response?.body?.error?.data ?? {};
    const { internal_message } = responseData;
    // NW account already has an active TU profile
    if (internal_message === 'credit profile already active for user') {
      return 'We already have this credit account on record.';
    }

    // User tried to activate a TU profile that is already active for a *different* NW account
    if (internal_message === 'Active TU Enrollment already exists for SSN') {
      return 'Hey, old friend! We have your credit score but it’s connected to a different email of yours. Please sign in with that account.';
    }

    if (internal_message === 'failed Schematics input validation') {
      return 'An invalid input was detected in your request. Please refresh the page and verify all your information is correct.';
    }

    if (
      internal_message === 'User has attempted authentication too many times'
    ) {
      return 'Too many unsuccessful verification attempts to connect to your account. Please try again in 30 days.';
    }

    // when a user is marked fraudulent and requires sms verification we don't want them to know that they've been marked.
    // silencing the error message gives the illusion of a regular flow
    if (internal_message === 'User requires SMS verification') {
      return;
    }
  }
  return 'We are unable to find your information at this point.';
};

type CreditScoreBracket = {
  /* Used as `credit_score_bracket_name` at segment event props */
  name: string;
  /* Used as `credit_score_bracket_value` at segment event props */
  value: string;
};

export const creditScoreBracketName = {
  POOR: 'poor',
  GOOD: 'good',
  AVERAGE: 'average',
  EXCELLENT: 'excellent',
  UNKNOWN: 'unknown',
};

export const scoreRangeByBracketName = {
  [creditScoreBracketName.POOR]: { min: 300, max: 629 },
  [creditScoreBracketName.AVERAGE]: { min: 630, max: 689 },
  [creditScoreBracketName.GOOD]: { min: 690, max: 719 },
  [creditScoreBracketName.EXCELLENT]: { min: 720, max: 850 },
};

export const getCreditScoreBracket = (score: number): CreditScoreBracket => {
  const match = Object.entries(scoreRangeByBracketName).find((p) => {
    const { min, max } = p[1];
    return score >= min && score <= max;
  });

  if (!match) return { name: 'unknown', value: 'unknown' };

  const [tier, range] = match;

  return { name: tier, value: `${range.min}-${range.max}` };
};

export const getSegmentTuAuthenticationSuccessEvent = (
  data: TuCreditReportIdentityProfile,
) => {
  const bracket = getCreditScoreBracket(data.vantageScore3);

  return {
    name: 'Credit Score Activated',
    props: {
      credit_bureau: 'transunion',
      credit_score_activated_date: new Date().toISOString(),
      credit_score_bracket_name: bracket.name,
      credit_score_bracket_value: bracket.value,
      destination_url: window.location.href,
      meaningful_engagement_category: 'Credit Score',
      meaningful_engagement_subcategory: 'Credit Score Summary',
      meaningful_engagement_flag: true,
      verification_type: 'full',
    },
  };
};
